/* eslint-disable react/forbid-dom-props */
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import {
  Kanban as KanbanIcon, Plus, GitCompare, CheckSquare,
  BarChart2, Settings, LogOut, Menu, X,
  Moon, Sun, ChevronLeft, ChevronRight, BookOpen, User as UserIcon,
} from 'lucide-react';

import './styles/globals.css';
import pkg from '../package.json';
import { AuthProvider, useAuthContext } from './auth/AuthContext';
import { ProtectedRoute } from './rbac/ProtectedRoute';
import { PERMISSIONS } from './rbac/permissions';
import { useAuthStore } from './store/useAuthStore';
import { useUIStore } from './store/useUIStore';
import { casesApi } from './services/api';
import toast from 'react-hot-toast';

import LoginPage from './pages/Login';
import FormularioFicha from './pages/FormularioFicha';
import Kanban from './pages/Kanban';
import Revisao from './pages/Revisao';
import Aprovacao from './pages/Aprovacao';
import Dashboard from './pages/Dashboard';
import Configuracoes from './pages/Configuracoes';
import UserAvatar from './components/UserAvatar';
import Perfil from './pages/Perfil';
import GuiaEquipe from './pages/GuiaEquipe';
import ImprimirFicha from './pages/FormularioFicha/ImprimirFicha';

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30000, retry: 1 } },
});

function Shell() {
  const { user } = useAuthStore();
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const { theme, sidebarCollapsed, toggleSidebar, toggleTheme } = useUIStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Global polling for cases
  const { data: cases = [] } = useQuery({
    queryKey: ['cases-kanban'],
    queryFn: casesApi.list,
    refetchInterval: (query) => {
      const hasGenerating = query?.state?.data?.some(c => c.status === 'gerando_peticao');
      return hasGenerating ? 5000 : false;
    },
    enabled: !!user,
  });

  const prevCasesRef = useRef([]);

  useEffect(() => {
    if (!cases || cases.length === 0) {
      prevCasesRef.current = [];
      return;
    }

    prevCasesRef.current.forEach(prev => {
      const curr = cases.find(c => c.case_id === prev.case_id);
      if (curr && prev.status === 'gerando_peticao' && curr.status !== 'gerando_peticao') {
        // Correção que falha devolve o caso à revisão com a MESMA versão (1.23): não é peça nova.
        if (curr.status !== 'ficha_preenchimento' && prev.petition_id && curr.petition_id === prev.petition_id) {
          casesApi.getGenerationStatus(curr.case_id).then(statusData => {
            toast.error(
              `A correção da petição de ${curr.client_name} não foi concluída: ${statusData.falha_geracao || statusData.error_message || 'Erro desconhecido.'}`,
              { duration: 10000 }
            );
          }).catch(() => {
            toast.error(`A correção da petição de ${curr.client_name} não foi concluída.`, { duration: 8000 });
          });
          return;
        }
        if (curr.status === 'aguardando_revisao') {
          toast.success(
            (t) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', padding: '.15rem' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Petição de {curr.client_name} Gerada!</span>
                <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>O rascunho está pronto para revisão.</span>
                <button
                  className="btn btn-gold btn-sm"
                  style={{ marginTop: '.25rem', alignSelf: 'flex-start', padding: '.2rem .5rem', fontSize: '.75rem' }}
                  onClick={() => {
                    toast.dismiss(t.id);
                    navigate('/revisao', { state: { caseId: curr.case_id } });
                  }}
                >
                  Ir para Revisão
                </button>
              </div>
            ),
            { duration: 8000 }
          );
        } else if (curr.status === 'ficha_preenchimento') {
          const novaPeticaoSalva = curr.petition_id
            && curr.petition_status === 'gerada_com_pendencias'
            && (curr.petition_id !== prev.petition_id
              || Number(curr.petition_version) > Number(prev.petition_version || 0));
          if (novaPeticaoSalva) {
            toast((t) => (
              <div>
                <strong>Petição v{curr.petition_version} gerada e salva, com pendências de revisão.</strong>
                <p>{curr.client_name}: confira o documento e os apontamentos antes de gerar outra versão.</p>
                <button className="btn btn-outline btn-sm" onClick={() => {
                  toast.dismiss(t.id);
                  navigate(`/ficha/${curr.case_id}`);
                }}>
                  Ver petição e pendências
                </button>
              </div>
            ), { duration: 12000, icon: '⚠️' });
            return;
          }
          casesApi.getGenerationStatus(curr.case_id).then(statusData => {
            toast.error(
              `Falha na geração da petição de ${curr.client_name}: ${statusData.error_message || 'Erro desconhecido.'}`,
              { duration: 10000 }
            );
          }).catch(() => {
            toast.error(
              `Falha na geração da petição de ${curr.client_name}.`,
              { duration: 8000 }
            );
          });
        }
      }
    });

    prevCasesRef.current = cases;
  }, [cases, navigate]);

  const navItems = [
    { to: '/guia', label: 'Guia da equipe', icon: BookOpen, section: 'Ajuda' },
    { to: '/ficha/novo',    label: 'Nova Ficha',     icon: Plus,         perm: PERMISSIONS.FICHA_CREATE,    section: 'Petições' },
    { to: '/casos',         label: 'Casos',           icon: KanbanIcon,   perm: PERMISSIONS.KANBAN_VIEW,     section: 'Petições' },
    { to: '/revisao',       label: 'Revisar',         icon: GitCompare,   perm: PERMISSIONS.PETITION_REVIEW, section: 'Revisão'  },
    { to: '/aprovacao',     label: 'Aprovação Final', icon: CheckSquare,  perm: PERMISSIONS.PETITION_APPROVE,section: 'Revisão'  },
    { to: '/dashboard',     label: 'Dashboard',       icon: BarChart2,    perm: PERMISSIONS.DASHBOARD_VIEW,  section: 'Gestão'   },
    { to: '/configuracoes', label: 'Configurações',   icon: Settings,     perm: PERMISSIONS.CONFIG_EDIT,     section: 'Gestão'   },
  ];

  const visibleItems = navItems.filter(
    item => !item.perm || user?.permissions?.includes(item.perm)
  );

  const sections = [...new Set(visibleItems.map(i => i.section))];

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <header className="app-header">
        <button
          className="btn btn-ghost btn-sm mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ display: 'none', padding: '.25rem', color: 'var(--text-on-dark)' }}
        >
          {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
        <div className="app-logo">Peticionador<span>BR</span></div>
        <div className="header-right" />
      </header>

      <aside className={`app-sidebar${sidebarOpen ? ' open' : ''}${sidebarCollapsed ? ' collapsed' : ''}`}>
        <div className="sidebar-header">
          <button
            className="sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {sidebarCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Navegação principal">
          {sections.map(section => (
            <div key={section} className="nav-section">
              <div className="nav-section-label">{section}</div>
              {visibleItems
                .filter(i => i.section === section)
                .map(item => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `nav-item${isActive ? ' active' : ''}`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon size={15} /> <span>{item.label}</span>
                    </NavLink>
                  );
                })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer" ref={userMenuRef}>
          <button
            type="button"
            className="user-widget"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            aria-haspopup="true"
            aria-expanded={userMenuOpen}
          >
            <UserAvatar user={user} size={32} />
            <div className="user-widget-info">
              <span className="user-name">{user?.name}</span>
              <span>{user?.role_label || user?.role}</span>
            </div>
          </button>

          {userMenuOpen && (
            <div className="user-popover" role="menu">
              <div className="user-popover-header">
                <div className="user-name">{user?.name}</div>
                <div className="user-email">{user?.email}</div>
                <div className="user-role">{user?.role_label || user?.role}</div>
                <div className="user-version">v{pkg.version}</div>
              </div>

              <button
                className="popover-item"
                onClick={() => { setUserMenuOpen(false); navigate('/perfil'); }}
              >
                <UserIcon size={14} /> Meu Perfil
              </button>

              <button
                className="popover-item"
                onClick={() => { toggleTheme(); }}
              >
                {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />} Tema {theme === 'light' ? 'Escuro' : 'Claro'}
              </button>

              <div className="popover-divider" />

              <button
                className="popover-item danger"
                onClick={() => { setUserMenuOpen(false); logout(); }}
              >
                <LogOut size={14} /> Sair
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className="app-main">
        <Routes>
          <Route path="/" element={<Navigate to="/casos" replace />} />
          <Route path="/ficha/novo" element={
            <ProtectedRoute permission={PERMISSIONS.FICHA_CREATE}>
              <FormularioFicha />
            </ProtectedRoute>
          } />
          <Route path="/ficha/:id" element={
            <ProtectedRoute permission={PERMISSIONS.FICHA_EDIT}>
              <FormularioFicha />
            </ProtectedRoute>
          } />
          <Route path="/kanban" element={<Navigate to="/casos" replace />} />
          <Route path="/casos" element={
            <ProtectedRoute permission={PERMISSIONS.KANBAN_VIEW}>
              <Kanban />
            </ProtectedRoute>
          } />
          <Route path="/revisao" element={
            <ProtectedRoute permission={PERMISSIONS.PETITION_REVIEW}>
              <Revisao />
            </ProtectedRoute>
          } />
          <Route path="/aprovacao" element={
            <ProtectedRoute permission={PERMISSIONS.PETITION_APPROVE}>
              <Aprovacao />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute permission={PERMISSIONS.DASHBOARD_VIEW}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/configuracoes" element={
            <ProtectedRoute permission={PERMISSIONS.CONFIG_EDIT}>
              <Configuracoes />
            </ProtectedRoute>
          } />
          <Route path="/guia" element={<GuiaEquipe />} />
          <Route path="/perfil" element={
            <Perfil />
          } />
          <Route path="/formulario/imprimir" element={
            <ImprimirFicha />
          } />
        </Routes>
      </main>

      <Toaster
        position="top-right"
        containerStyle={{ top: 'calc(var(--header-h, 56px) + 10px)' }}
        toastOptions={{
          style: {
            fontFamily: 'var(--font-body)',
            fontSize: '.875rem',
            borderRadius: 'var(--radius-md)',
          },
          success: { iconTheme: { primary: '#2d6a4f', secondary: '#fff' } },
          error:   { iconTheme: { primary: '#c0392b', secondary: '#fff' } },
        }}
      />
    </div>
  );
}

function AppRouter() {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return <Shell />;
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
